import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/ui/dialog';
import { Button } from '@/ui/Button';
import { supabaseService, type Repository } from '@/shared/services/supabaseService';
import { toast } from 'sonner';
import { SelectedReposList } from './SelectedReposList';
import { RepoSelectionCombobox } from './RepoSelectionCombobox';

interface RepositorySelectorProps {
  isOpen: boolean;
  onClose: () => void;
  onApply: (repositoryIds: string[]) => void;
  ruleTitle: string;
}

const RepositorySelector: React.FC<RepositorySelectorProps> = ({
  isOpen,
  onClose,
  onApply,
  ruleTitle
}) => {
  const [repositories, setRepositories] = useState<Repository[]>([]);
  const [selectedRepositories, setSelectedRepositories] = useState<string[]>([]);
  const [popoverOpen, setPopoverOpen] = useState(false);
  const [loading, setLoading] = useState(false);

  // Fetch repositories when dialog opens
  useEffect(() => {
    if (isOpen) {
      const fetchRepositories = async () => {
        try {
          const repos = await supabaseService.getRepositories();
          setRepositories(repos);
        } catch (error) {
          console.error('Error fetching repositories:', error);
          toast.error('Failed to load repositories');
        }
      };
      fetchRepositories();
    }
  }, [isOpen]);

  const handleApply = async () => {
    if (selectedRepositories.length === 0) {
      toast.error('Please select at least one repository');
      return;
    }

    setLoading(true);
    try {
      onApply(selectedRepositories);
      onClose();
    } catch (error) {
      console.error('Error applying rule:', error);
      toast.error('Failed to apply rule');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setSelectedRepositories([]);
    setPopoverOpen(false);
    onClose();
  };

  const toggleRepoSelection = (repoId: string) => {
    setSelectedRepositories(prev =>
        prev.includes(repoId)
            ? prev.filter(id => id !== repoId)
            : [...prev, repoId]
    );
  };

  const removeRepo = (repoId: string) => {
      setSelectedRepositories(prev => prev.filter(id => id !== repoId));
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto rounded-none border-2 border-neutral-950 dark:border-neutral-100 p-0 gap-0 bg-background">
        <DialogHeader className="p-6 border-b border-neutral-950 dark:border-neutral-100 bg-neutral-50 dark:bg-neutral-900/10">
          <DialogTitle className="font-mono text-xl uppercase font-bold tracking-tight">Apply Rule to Repositories</DialogTitle>
          <DialogDescription className="font-mono text-xs text-muted-foreground mt-2">
            Select repositories where you want to apply the rule <span className="font-bold text-foreground">"{ruleTitle}"</span>.
          </DialogDescription>
        </DialogHeader>

        <div className="p-6 space-y-6">
          <SelectedReposList
            repositories={repositories}
            selectedRepositories={selectedRepositories}
            onRemove={removeRepo}
          />

          <RepoSelectionCombobox
            repositories={repositories}
            selectedRepositories={selectedRepositories}
            onSelect={toggleRepoSelection}
            open={popoverOpen}
            setOpen={setPopoverOpen}
          />

          {/* Actions */}
          <div className="flex justify-end gap-3 pt-4 border-t border-neutral-100 dark:border-neutral-800">
            <Button
                type="button"
                variant="outline"
                onClick={handleClose}
                className="rounded-none font-mono uppercase text-xs border-neutral-200 dark:border-neutral-800"
            >
              Cancel
            </Button>
            <Button
              type="button"
              onClick={handleApply}
              disabled={loading || selectedRepositories.length === 0}
              className="rounded-none font-mono uppercase text-xs font-bold bg-neutral-950 text-white dark:bg-white dark:text-neutral-950 hover:bg-neutral-800 dark:hover:bg-neutral-200"
            >
              {loading ? 'APPLYING...' : `APPLY TO ${selectedRepositories.length} REPO(S)`}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default RepositorySelector;
